import { useEffect, useState, useMemo } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getSops, GetSopsOutputType } from 'zite-endpoints-sdk';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, FileText, BookOpen } from 'lucide-react';
import SOPDetailView from '@/components/sop/SOPDetailView';

type SOP = GetSopsOutputType['sops'][0];

export default function SOPLibrary() {
  const { user } = useAuth();
  const [sops, setSops] = useState<SOP[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    getSops({}).then(d => {
      setSops(d.sops);
      setCategories(d.categories);
      setDepartments(d.departments);
    }).finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return sops.filter(s => {
      if (catFilter !== 'all' && s.category !== catFilter) return false;
      if (deptFilter !== 'all' && !s.departments.includes(deptFilter) && !s.departments.includes('All')) return false;
      if (q && !s.title.toLowerCase().includes(q) && !s.content.toLowerCase().includes(q) && !s.category.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sops, search, catFilter, deptFilter]);

  const selected = selectedId ? sops.find(s => s.id === selectedId) || null : null;
  const relatedSops = selected
    ? sops.filter(s => s.id !== selected.id && (s.category === selected.category || s.tags.some(t => selected.tags.includes(t)))).slice(0, 5)
    : [];

  if (selected) {
    return (
      <div className="min-h-screen bg-background">
        <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto">
          <SOPDetailView
            sop={selected}
            relatedSops={relatedSops}
            onBack={() => setSelectedId(null)}
            onSelectRelated={id => setSelectedId(id)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 md:px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-foreground">SOP Library</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Standard operating procedures — quick reference for your role</p>
          </div>
          <div className="relative w-56 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="Search SOPs..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
          </div>
        </div>
        {/* Filters */}
        <div className="flex gap-3 mt-4 flex-wrap">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground self-center">{filtered.length} SOPs</span>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              {sops.length === 0 ? 'No SOPs available yet.' : 'No SOPs match your filters.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(sop => {
              const statusColor = sop.sopStatus === 'Published' ? 'text-green-700 bg-green-500/10'
                : sop.sopStatus === 'Draft' ? 'text-yellow-700 bg-yellow-500/10'
                : 'text-muted-foreground bg-muted';
              return (
                <button
                  key={sop.id}
                  onClick={() => setSelectedId(sop.id)}
                  className="border border-border bg-card p-4 rounded-lg text-left hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <FileText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <Badge variant="outline" className={`text-xs ${statusColor}`}>{sop.sopStatus}</Badge>
                  </div>
                  <p className="font-medium text-foreground text-sm group-hover:text-primary transition-colors">{sop.title}</p>
                  {sop.category && (
                    <p className="text-xs text-muted-foreground mt-1">{sop.category}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {sop.departments.slice(0, 2).map(d => (
                      <span key={d} className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{d}</span>
                    ))}
                  </div>
                  <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">View SOP →</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
